// src/components/pos/ui/Button.jsx
// POS-specific button components optimized for touch interfaces

import { forwardRef } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '../../../utils/cn'

// Base POS Button - Large touch-friendly button
const POSButton = forwardRef(({ 
  className, 
  variant = "default", 
  size = "default", 
  loading = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  children,
  disabled,
  ...props 
}, ref) => {
  const variants = {
    default: "bg-white text-gray-900 border border-gray-300 hover:bg-gray-50",
    primary: "bg-orange-500 text-white hover:bg-orange-600 border-orange-500",
    secondary: "bg-gray-500 text-white hover:bg-gray-600 border-gray-500",
    success: "bg-green-500 text-white hover:bg-green-600 border-green-500",
    danger: "bg-red-500 text-white hover:bg-red-600 border-red-500",
    warning: "bg-yellow-500 text-white hover:bg-yellow-600 border-yellow-500",
    ghost: "bg-transparent text-gray-700 hover:bg-gray-100 border-transparent",
    outline: "bg-transparent border border-gray-300 text-gray-700 hover:bg-gray-50"
  }

  const sizes = {
    sm: "h-10 px-4 text-sm min-w-[80px]",
    default: "h-12 px-6 text-base min-w-[120px]", 
    lg: "h-14 px-8 text-lg min-w-[140px]",
    xl: "h-16 px-10 text-xl min-w-[160px]",
    icon: "h-12 w-12 p-0"
  }

  return (
    <button
      className={cn(
        // Base styles - optimized for touch
        "inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:ring-offset-2",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        "select-none touch-manipulation", // Prevents text selection and improves touch response
        "active:scale-95", // Touch feedback
        variants[variant],
        sizes[size],
        className
      )}
      disabled={disabled || loading}
      ref={ref}
      {...props}
    >
      {loading && (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      )}
      {!loading && LeftIcon && (
        <LeftIcon className="mr-2 h-5 w-5" />
      )}
      {children}
      {!loading && RightIcon && (
        <RightIcon className="ml-2 h-5 w-5" />
      )}
    </button>
  )
})

// Product Category Button
const CategoryButton = forwardRef(({ 
  active = false, 
  count,
  className,
  children,
  ...props 
}, ref) => {
  return (
    <POSButton
      ref={ref}
      variant={active ? "primary" : "outline"}
      size="default"
      className={cn(
        "flex-col h-16 w-full relative",
        active && "ring-2 ring-orange-300",
        className
      )}
      {...props}
    >
      <span className="font-medium">{children}</span>
      {count !== undefined && (
        <span className="text-xs opacity-80 mt-1">({count})</span>
      )}
    </POSButton>
  )
})

// Product Button for product grid
const ProductButton = forwardRef(({ 
  product,
  className,
  onAddToCart,
  ...props 
}, ref) => {
  return (
    <button
      ref={ref}
      className={cn(
        "bg-white border border-gray-200 rounded-lg p-4 h-24",
        "hover:border-orange-300 hover:shadow-md transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-orange-500/50",
        "text-left w-full touch-manipulation active:scale-95",
        className
      )}
      onClick={() => onAddToCart?.(product)}
      {...props}
    >
      <div className="flex flex-col h-full">
        <h3 className="font-medium text-gray-900 text-sm truncate">
          {product?.name || 'Product Name'}
        </h3>
        <p className="text-orange-600 font-bold text-base mt-auto">
          Rs.{product?.outletPrice?.toFixed(2) || '0.00'}
        </p>
      </div>
    </button>
  )
})

// Number Pad Button
const NumberButton = forwardRef(({ 
  value,
  className,
  variant = "default",
  ...props 
}, ref) => {
  const numberVariants = {
    default: "bg-gray-100 text-gray-900 hover:bg-gray-200 border-gray-300",
    action: "bg-orange-500 text-white hover:bg-orange-600 border-orange-500",
    clear: "bg-red-500 text-white hover:bg-red-600 border-red-500",
    enter: "bg-green-500 text-white hover:bg-green-600 border-green-500"
  }

  return (
    <button
      ref={ref}
      className={cn(
        "h-14 w-14 rounded-lg border font-bold text-lg",
        "transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500/50",
        "touch-manipulation active:scale-95 select-none",
        numberVariants[variant],
        className
      )}
      {...props}
    >
      {value}
    </button>
  )
})

// Quick Amount Button for payments
const QuickAmountButton = forwardRef(({ 
  amount,
  currency = "Rs.",
  active = false,
  className,
  ...props 
}, ref) => {
  return (
    <button
      ref={ref}
      className={cn(
        "h-12 px-4 rounded-lg border font-semibold transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-orange-500/50",
        "touch-manipulation active:scale-95",
        active 
          ? "bg-orange-500 text-white border-orange-500" 
          : "bg-gray-100 text-gray-900 hover:bg-gray-200 border-gray-300",
        className
      )}
      {...props}
    >
      {currency}{amount}
    </button>
  )
})

// Order Type Button (Dine-in, Takeaway, Delivery)
const OrderTypeButton = forwardRef(({ 
  type,
  active = false,
  icon: Icon,
  className,
  ...props 
}, ref) => {
  return (
    <button
      ref={ref}
      className={cn(
        "flex items-center justify-center h-12 px-6 rounded-lg border font-medium transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-orange-500/50",
        "touch-manipulation active:scale-95",
        active 
          ? "bg-orange-500 text-white border-orange-500" 
          : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300",
        className
      )}
      {...props}
    >
      {Icon && <Icon className="mr-2 h-4 w-4" />}
      {type}
    </button>
  )
})

// Payment Method Button
const PaymentMethodButton = forwardRef(({ 
  method,
  icon: Icon,
  active = false,
  className,
  ...props 
}, ref) => {
  return (
    <button
      ref={ref}
      className={cn(
        "flex flex-col items-center justify-center h-20 w-24 rounded-lg border transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-orange-500/50",
        "touch-manipulation active:scale-95",
        active 
          ? "bg-orange-500 text-white border-orange-500" 
          : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300",
        className
      )}
      {...props}
    >
      {Icon && <Icon className="h-6 w-6 mb-1" />}
      <span className="text-xs font-medium">{method}</span>
    </button>
  )
})

// Table Button for table selection
const TableButton = forwardRef(({ 
  tableNumber,
  status = "available", // available, occupied, reserved
  className,
  ...props 
}, ref) => {
  const statusStyles = {
    available: "bg-white text-gray-900 border-gray-300 hover:bg-gray-50",
    occupied: "bg-red-500 text-white border-red-500",
    reserved: "bg-green-500 text-white border-green-500"
  }

  const statusLabels = {
    available: "Available",
    occupied: "Occupied", 
    reserved: "Reserved"
  }

  return (
    <button
      ref={ref}
      className={cn(
        "flex flex-col items-center justify-center h-20 w-20 rounded-lg border-2 font-bold transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-orange-500/50",
        "touch-manipulation active:scale-95",
        statusStyles[status],
        status === "occupied" && "cursor-not-allowed",
        className
      )}
      disabled={status === "occupied"}
      {...props}
    >
      <span className="text-lg">{tableNumber}</span>
      <span className="text-xs font-normal mt-1">{statusLabels[status]}</span>
    </button>
  )
})

// Action Button for main POS actions
const ActionButton = forwardRef(({ 
  icon: Icon,
  label,
  sublabel,
  variant = "default",
  className,
  ...props 
}, ref) => {
  const actionVariants = {
    default: "bg-white text-gray-900 border-gray-300 hover:bg-gray-50",
    primary: "bg-orange-500 text-white border-orange-500 hover:bg-orange-600",
    success: "bg-green-500 text-white border-green-500 hover:bg-green-600",
    danger: "bg-red-500 text-white border-red-500 hover:bg-red-600"
  }

  return (
    <button
      ref={ref}
      className={cn(
        "flex flex-col items-center justify-center h-20 px-4 rounded-lg border transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-orange-500/50",
        "touch-manipulation active:scale-95",
        actionVariants[variant],
        className
      )}
      {...props}
    >
      {Icon && <Icon className="h-6 w-6 mb-1" />}
      <span className="font-medium text-sm">{label}</span>
      {sublabel && (
        <span className="text-xs opacity-80">{sublabel}</span>
      )}
    </button>
  )
})

POSButton.displayName = "POSButton"
CategoryButton.displayName = "CategoryButton"
ProductButton.displayName = "ProductButton"
NumberButton.displayName = "NumberButton"
QuickAmountButton.displayName = "QuickAmountButton"
OrderTypeButton.displayName = "OrderTypeButton"
PaymentMethodButton.displayName = "PaymentMethodButton"
TableButton.displayName = "TableButton"
ActionButton.displayName = "ActionButton"

export {
  POSButton,
  CategoryButton,
  ProductButton,
  NumberButton,
  QuickAmountButton,
  OrderTypeButton,
  PaymentMethodButton,
  TableButton,
  ActionButton
}